import { faSpinner, faTrash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography
} from "@mui/material"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import Button from "components/Button"
import PaperBox from "components/PaperBox"
import theme from "css/theme/light"
import { Formik, FormikProps } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { useStyles } from "pages/violation/components/useStyles"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  IAdminSettingProps,
  IEmailListItem,
  ISelectListItem,
  IViolationEmailSettingsProps
} from "../violation.interface"

const AdminEmailSetting: React.FC<IAdminSettingProps> = (props) => {
  const { companyId } = props
  const [violationGetResult, violationGetApi] = useApi() as [IApiResult, IApiFunc]
  const [violationEmailSettingResult, violationEmailSettingApi] = useApi() as [IApiResult, IApiFunc]
  const [departmentResult, departmentApi] = useApi() as [IApiResult, IApiFunc]
  const [usersResult, getUsers] = useApi() as [IApiResult, IApiFunc]

  const formRef = useRef<FormikProps<any>>(null)
  const initialValues =
    violationGetResult.status === 2
      ? {
        ...violationGetResult.data
      }
      : {
        emails: [],
        departmentId: 0
      }

  const [selectedDepartment, setDepartment] = useState<number>()
  const [departments, setDepartments] = useState<ISelectListItem[] | undefined>()
  const [selectedReceivers, setSelectedReceiver] = useState<Array<IEmailListItem> | undefined>([])
  const { t } = useTranslation()

  useEffect(() => {
    if (initialValues.emails && initialValues.emails.length > 0) {
      setSelectedReceiver(initialValues.emails)
    }
  }, [initialValues.emails])

  useEffect(() => {
    getUsers.get(
      apiEndpoints.violationadmin.getuserstoassign.replace(
        "{departmentid}",
        selectedDepartment?.toString() || "0"
      )
    )
  }, [selectedDepartment])

  useEffect(() => {
    departmentApi.get(
      apiEndpoints.violationadmin.getavailabledepartments.replace("{companyId}", companyId.toString())
    )
  }, [])

  useEffect(() => {
    if (departmentResult.status === 2) {
      setDepartments(departmentResult.data)
    }
  }, [departmentResult])

  useEffect(() => {
    if (violationGetResult.status === 2) {
      formRef?.current?.setFieldValue("emails", violationGetResult.data?.emails || [])
    }
  }, [violationGetResult.status])

  useEffect(() => {
    violationGetApi.get(
      apiEndpoints.violationadmin.getviolationemailsettings.replace(
        "{departmentid}",
        selectedDepartment?.toString() || "0"
      )
    )
  }, [selectedDepartment])

  useEffect(() => {
    setSelectedReceiver([])
  }, [selectedDepartment])

  const handleSubmit = (values: IViolationEmailSettingsProps) => {
    const violationValues: IViolationEmailSettingsProps = {
      ...values,
      departmentId: selectedDepartment || 0
    }
    violationEmailSettingApi.post(apiEndpoints.violationadmin.postviolationemailsetting, violationValues)
  }

  const removeReceiver = (
    setFieldValue: any,
    values: IViolationEmailSettingsProps,
    userId: number
  ) => {
    const nextDeptEmails = (values.emails ?? []).filter((x) => x.userId !== userId)
    setFieldValue("emails", nextDeptEmails)
    setSelectedReceiver((prev) => prev?.filter((x) => x.userId !== userId))
  }

  const classes = useStyles()
  return (
    <PaperBox>
      <Formik onSubmit={handleSubmit} initialValues={initialValues} innerRef={formRef}>
        {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, errors, touched, setFieldTouched }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Grid container direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Grid item xs={12} mb={3}>
                <Grid item xs={12} marginBottom={2}>
                  <Typography variant="labelLarge" fontWeight={500}>
                    {t(`violation.admin.email_info_text`, `Add email addresses to get notified about violations`)}
                  </Typography>
                </Grid>
                <List sx={{ p: 0 }}>
                  <Box bgcolor={theme.palette.grey[100]} sx={{ borderRadius: "6px" }} padding={3} width="100%">
                    <Box className={classes.fullWidth}>
                      <ListItemText
                        classes={{
                          secondary: classes.listItemText,
                          primary: classes.listItemTextPrimary
                        }}
                        primary={t("violation.select_department", "Select department")}
                        style={{ paddingBottom: "8px", margin: 0 }}
                      />
                    </Box>
                    <ListItem
                      style={{
                        padding: 0,
                        marginBottom: 16,
                        border: `1px solid ${theme.palette.grey["100"]}`,
                        borderRadius: 4
                      }}>
                      <Grid container direction="row" justifyContent="space-between" alignItems="center">
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <Autocomplete
                              sx={{ width: "100%", background: theme.palette.background.paper }}
                              options={departments || []}
                              getOptionLabel={(option: any) => option?.name}
                              value={departments?.find((x: any) => x.id === selectedDepartment) || null}
                              onChange={(e, option) => setDepartment(option?.id)}
                              renderInput={(params) => (
                                <TextField {...params} name={"reason"} variant="outlined" placeholder={t("global.department", "Department")} />
                              )}
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                    </ListItem>
                    <Box className={classes.fullWidth}>
                      <ListItemText
                        classes={{
                          secondary: classes.listItemText,
                          primary: classes.listItemTextPrimary
                        }}
                        primary={t("global.add_recipients", "Add recipients")}
                        style={{ paddingBottom: "8px", margin: 0 }}
                      />
                    </Box>
                    <ListItem
                      style={{
                        padding: 0,
                        border: `1px solid ${theme.palette.grey["100"]}`,
                        borderRadius: 4
                      }}>
                      <Grid container direction="row" justifyContent="space-between" alignItems="center" flexWrap="nowrap" gap={2}>
                        <Grid item xs={10}>
                          {usersResult.status === 2 ? (
                            <Autocomplete
                              placeholder={t("global.add_recipients", "Add recipients")}
                              style={{ minWidth: 250, background: "white" }}
                              id="receiver"
                              options={usersResult.data || []}
                              getOptionLabel={(option: IEmailListItem) => option?.name ?? ""}
                              getOptionDisabled={(option: IEmailListItem) =>
                                values?.emails?.findIndex((x) => x?.userId === option.userId) !== -1
                              }

                              onChange={(e, value) => {
                                if (value) {
                                  setSelectedReceiver((prev) => [...(prev ?? []), value])
                                }
                              }}

                              renderOption={(props, option) => (
                                <li {...props}>
                                  <Stack direction="row" spacing={2} justifyContent="space-between">
                                    <Box sx={{ fontWeight: 600, minWidth: "100px" }}>{option.name}</Box>
                                    <Box sx={{ opacity: 0.6 }}>{option.email}</Box>
                                  </Stack>
                                </li>
                              )}

                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={t("global.add_recipients", "Add recipients")}
                                  variant="outlined"
                                  error={Boolean(touched.emails && errors.emails)}
                                  helperText={
                                    touched.emails && errors.emails
                                    && errors.emails
                                  }
                                  FormHelperTextProps={{
                                    sx: {
                                      backgroundColor: theme.palette.grey["100"],
                                      margin: 0,
                                      padding: "3px 14px"
                                    },
                                  }}
                                />

                              )}
                            />

                          ) : <FontAwesomeIcon icon={faSpinner} spin color={(theme.palette as any).primary[700]} />}
                        </Grid>
                        <Button color="primary" type="button" variant="outlined" size="small" onClick={() => {
                          setFieldValue("emails", selectedReceivers)
                          setFieldTouched("emails", true, true)
                        }}>
                          {t("global.add", "Add")}
                        </Button>
                      </Grid>
                    </ListItem>
                  </Box>
                  <Box>
                  </Box>
                  {selectedDepartment !== undefined && <Box>
                    {usersResult.status === 2 ?
                      <Box>
                        <Typography mt={2} variant="h5">{departments?.find((x) => x.id === selectedDepartment)?.name}</Typography>
                        {(values?.emails?.length ?? 0) > 0 ? (
                          <ListItem
                            style={{
                              padding: 0,
                              margin: "16px 0 0 0",
                              border: `1px solid ${theme.palette.grey[200]}`,
                              borderRadius: 6,
                            }}>
                            <Box display="flex" flexDirection="column" borderColor={(theme.palette as any).grey[200]} width="100%">
                              {values?.emails?.map((data: IEmailListItem, index: number) => (
                                <Grid
                                  key={index}
                                  borderBottom={index === (values.emails?.length ?? 0) - 1 ? 0 : 1}
                                  borderColor={(theme.palette as any).grey[200]}
                                  p={1.5}
                                  container
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Grid item xs={11}>
                                    <Typography variant="bodySmall" fontWeight={600}>{data.name} ({data.userId})</Typography>
                                    <Typography variant="bodySmall">{data.email}</Typography>
                                  </Grid>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeReceiver(setFieldValue, values, data.userId)}
                                    color="error">
                                    <FontAwesomeIcon color={theme.palette.error.main} icon={faTrash} />
                                  </IconButton>
                                </Grid>
                              ))}
                            </Box>
                          </ListItem>
                        ) : <Typography mt={1}>{t("violation.no_recipients_added", "No recipients added")}</Typography>}
                      </Box> : <Box mt={1}>
                        <FontAwesomeIcon icon={faSpinner} spin color={(theme.palette as any).primary[700]} />
                      </Box>
                    }
                  </Box>}
                </List>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  result={violationEmailSettingResult}>
                  {t("global.save", "Save")}
                </Button>
              </Box>
            </Grid>
          </form>
        )}
      </Formik>
    </PaperBox>
  )
}

export default AdminEmailSetting
